import React from 'react'
import { Helmet } from 'react-helmet-async'
import { useLocation } from 'react-router-dom'
import {
  SITE_URL,
  LANGUAGES,
  DEFAULT_LANGUAGE,
  PAGE_ROUTES,
  getLanguageFromPath,
  getPageIdFromPath,
  getHreflangAlternates,
} from '../i18n/routes'

export default function SeoHead({
  pageId,
  lang,
  title,
  description,
  ogImage = 'https://assets.auxodev.dk/assets/794f847f-602c-4879-a337-d3ce1466cad5/cake-05.jpg',
  noIndex = false,
}) {
  const location = useLocation()
  const currentPath = location.pathname

  // Resolve active language & page definition
  const activeLang = lang || getLanguageFromPath(currentPath) || DEFAULT_LANGUAGE
  const resolvedPageId = pageId || getPageIdFromPath(currentPath)
  const pageConfig = resolvedPageId ? PAGE_ROUTES[resolvedPageId] : null

  // Resolve title & description
  const defaultSeo = pageConfig?.seo?.[activeLang] || {}
  const finalTitle = title || defaultSeo.title || 'Fanø Chokolade'
  const finalDescription =
    description ||
    defaultSeo.description ||
    'Håndlavet chokolade og café på Fanø ved Fanø Bad. Smag, hygge og kvalitet.'

  // Resolve Canonical URL (strictly matching the current language's canonical path)
  const canonicalPath =
    pageConfig?.paths?.[activeLang] || currentPath.replace(/\/+$/, '') || '/'
  const canonicalUrl = `${SITE_URL}${canonicalPath}`

  // Resolve Hreflang Alternates (cross-linking all 3 languages + x-default)
  const hreflang = getHreflangAlternates(resolvedPageId, currentPath)
  const langMeta = LANGUAGES[activeLang] || LANGUAGES[DEFAULT_LANGUAGE]

  return (
    <Helmet>
      {/* HTML Language attribute */}
      <html lang={activeLang} />

      {/* Primary Meta Tags */}
      <title>{finalTitle}</title>
      <meta name="description" content={finalDescription} />
      {noIndex && <meta name="robots" content="noindex, nofollow" />}

      {/* Canonical Link */}
      <link rel="canonical" href={canonicalUrl} />

      {/* Hreflang Alternates across all supported languages */}
      <link rel="alternate" hrefLang="da" href={hreflang.da} />
      <link rel="alternate" hrefLang="en" href={hreflang.en} />
      <link rel="alternate" hrefLang="de" href={hreflang.de} />
      <link rel="alternate" hrefLang="x-default" href={hreflang['x-default']} />

      {/* Open Graph / Facebook */}
      <meta property="og:site_name" content="Fanø Chokolade" />
      <meta property="og:type" content="website" />
      <meta property="og:url" content={canonicalUrl} />
      <meta property="og:title" content={finalTitle} />
      <meta property="og:description" content={finalDescription} />
      <meta property="og:image" content={ogImage} />
      <meta property="og:locale" content={langMeta.locale} />

      {/* Twitter Cards */}
      <meta name="twitter:card" content="summary_large_image" />
      <meta name="twitter:url" content={canonicalUrl} />
      <meta name="twitter:title" content={finalTitle} />
      <meta name="twitter:description" content={finalDescription} />
      <meta name="twitter:image" content={ogImage} />
    </Helmet>
  )
}
